import { useGetList } from "ra-core";
import { Gauge } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * "Your plan" - seats and contact entitlements with live usage (decision
 * #17). Limits are managed by Grey Matter from the operator console; the
 * tenant sees exactly where they stand and who to call to change it.
 */

const Meter = ({
  label,
  used,
  limit,
}: {
  label: string;
  used: number;
  limit: number;
}) => {
  const pct = limit > 0 ? used / limit : 0;
  const over = pct > 1;
  const warn = !over && pct >= 0.8;
  const color = over ? "#c97a63" : warn ? "#d9b36b" : "#2e6ba9";
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between text-sm">
        <span>{label}</span>
        <span className="text-muted-foreground text-xs">
          <span className="font-semibold text-foreground text-sm">
            {used.toLocaleString("en-US")}
          </span>{" "}
          of {limit.toLocaleString("en-US")}
        </span>
      </div>
      <div className="h-2 bg-muted rounded-sm overflow-hidden">
        <div
          className="h-full rounded-sm transition-all"
          style={{ width: `${Math.min(100, pct * 100)}%`, backgroundColor: color }}
        />
      </div>
      {over && (
        <p className="text-xs text-[#c97a63] font-medium">
          Over plan - flagged to Grey Matter. Nothing breaks, but let's talk
          about the right tier.
        </p>
      )}
      {warn && (
        <p className="text-xs text-[#8a6d2f] dark:text-[#d9b36b]">
          Approaching your limit.
        </p>
      )}
    </div>
  );
};

export const PlanUsageCard = () => {
  const { planLimits } = useConfigurationContext();
  const { total: contactCount } = useGetList("contacts", {
    pagination: { page: 1, perPage: 1 },
  });
  const { total: userCount } = useGetList("sales", {
    pagination: { page: 1, perPage: 1 },
    filter: { "disabled@neq": true },
  });

  return (
    <Card id="plan">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground flex items-center gap-2">
          <Gauge className="w-5 h-5" /> Your plan
        </h2>
        <p className="text-sm text-muted-foreground">
          Your workspace includes {planLimits.maxUsers} user seat
          {planLimits.maxUsers === 1 ? "" : "s"} and{" "}
          {planLimits.maxContacts.toLocaleString("en-US")} contacts. Limits are
          managed by Grey Matter - reach out any time to change them.
        </p>
        <div className="flex flex-col gap-4">
          <Meter label="User seats" used={userCount ?? 0} limit={planLimits.maxUsers} />
          <Meter
            label="Contacts"
            used={contactCount ?? 0}
            limit={planLimits.maxContacts}
          />
        </div>
      </CardContent>
    </Card>
  );
};
